<script setup lang="ts">
import { onMounted, ref } from 'vue';

// Flat autumn leaves, drawn stem down. 6 shapes and 5 colours cycle out of step, so neighbouring leaves never match
const shapes = [
  {
    body: 'M12 1 L14 6 L18 4 L17 9 L22 9 L18.5 13 L20 16 L15 15.5 L13 19 L11 19 L9 15.5 L4 16 L5.5 13 L2 9 L7 9 L6 4 L10 6 Z',
    veins: 'M12 23 L12 3 M12 14 L18.5 9.5 M12 14 L5.5 9.5 M12 17 L16.5 15 M12 17 L7.5 15',
  },
  {
    body: 'M12 1 C18 6 19 14 12 21 C5 14 6 6 12 1 Z',
    veins:
      'M12 23 L12 3 M12 10 L15.5 7.5 M12 14 L16 11 M12 10 L8.5 7.5 M12 14 L8 11 M12 18 L14.5 15.5 M12 18 L9.5 15.5',
  },
  {
    body: 'M12 1 C16 5 17 9 15 11 C18 12 17 16 14 17 C14 19 13 20 12 21 C11 20 10 19 10 17 C7 16 6 12 9 11 C7 9 8 5 12 1 Z',
    veins: 'M12 23 L12 3 M12 9 L14.5 7 M12 9 L9.5 7 M12 14 L15.5 13 M12 14 L8.5 13',
  },
  {
    body: 'M12 1 C15 7 14.5 15 12 21 C9.5 15 9 7 12 1 Z',
    veins: 'M12 23 L12 3 M12 9 L13.8 7.5 M12 9 L10.2 7.5 M12 14 L14 12.5 M12 14 L10 12.5',
  },
  {
    body: 'M12 1 C14 1 14 3 13.5 4 C16 3 17 5 15 7 C18 7 18.5 10 15.5 11 C18 12 17.5 15 14.5 15 C15 17.5 13.5 19 12 20 C10.5 19 9 17.5 9.5 15 C6.5 15 6 12 8.5 11 C5.5 10 6 7 9 7 C7 5 8 3 10.5 4 C10 3 10 1 12 1 Z',
    veins: 'M12 23 L12 3 M12 7 L15 7 M12 7 L9 7 M12 11 L15.5 11 M12 11 L8.5 11 M12 15 L14.5 15 M12 15 L9.5 15',
  },
  {
    body: 'M12 2 C17 4 19 9 18 13 C17 17 14 19 12 21 C10 19 7 17 6 13 C5 9 7 4 12 2 Z',
    veins: 'M12 23 L12 4 M12 9 L16 7.5 M12 9 L8 7.5 M12 13 L17 11.5 M12 13 L7 11.5 M12 17 L15 16 M12 17 L9 16',
  },
];
const colors = ['#FE932B', '#A83506', '#FCCA2F', '#EB8C2B', '#D24D17'];
const leaves = Array.from({ length: 20 }, (_, i) => ({
  ...shapes[i % shapes.length],
  color: colors[i % colors.length],
}));

const root = ref<HTMLElement>();
// Every copy runs on the same clock, so the one clipped inside a sticky header lines up with the one behind the page
onMounted(() => {
  root.value?.getAnimations({ subtree: true }).forEach((animation) => {
    animation.startTime = 0;
  });
});
</script>

<template>
  <div
    ref="root"
    class="falling-leaves position-fixed top-0 right-0 bottom-0 left-0 overflow-hidden"
    aria-hidden="true"
  >
    <div v-for="(leaf, i) in leaves" :key="i" class="leaf position-absolute top-0 left-0">
      <div class="swirl w-100 h-100">
        <svg class="tumble w-100 h-100" viewBox="0 0 24 24">
          <path :d="leaf.body" :fill="leaf.color" />
          <path :d="leaf.veins" class="vein" />
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:math';

.falling-leaves {
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
}

.leaf {
  width: var(--size);
  height: var(--size);
}

.vein {
  fill: none;
  stroke: #692912;
  stroke-opacity: 0.6;
  stroke-width: 0.8;
  stroke-linecap: round;
}

// A leaf is three layers, each with its own motion over the same cycle (it lands at 50%):
// .leaf falls with the wind, .swirl swings and spins on the way down, .tumble turns over in 3D.
// Each motion sits on its own element: stacked on one element they would rotate each other's movement.
.swirl {
  perspective: 400px;
}

// Pendulum swings with a full spin in the middle, and a small loop when --loop is set.
// Every keyframe keeps the same transform list so the browser interpolates each part on its own.
@keyframes swirl {
  0% {
    transform: translateX(0) rotate(0deg) translateY(0) rotate(0deg);
    animation-timing-function: ease-in-out;
  }
  8% {
    transform: translateX(var(--amp)) rotate(0deg) translateY(0) rotate(35deg);
    animation-timing-function: ease-in-out;
  }
  16% {
    transform: translateX(calc(var(--amp) * -1)) rotate(0deg) translateY(0) rotate(-35deg);
    animation-timing-function: ease-in-out;
  }
  20% {
    transform: translateX(0) rotate(0deg) translateY(var(--loop)) rotate(0deg);
    animation-timing-function: linear;
  }
  28% {
    transform: translateX(0) rotate(360deg) translateY(var(--loop)) rotate(0deg);
    animation-timing-function: ease-in-out;
  }
  36% {
    transform: translateX(var(--amp)) rotate(360deg) translateY(0) rotate(35deg);
    animation-timing-function: ease-in-out;
  }
  44% {
    transform: translateX(calc(var(--amp) * -1)) rotate(360deg) translateY(0) rotate(-35deg);
    animation-timing-function: ease-in-out;
  }
  50% {
    transform: translateX(0) rotate(360deg) translateY(0) rotate(0deg);
    animation-timing-function: ease-in-out;
  }
  60% {
    transform: translateX(4px) rotate(360deg) translateY(0) rotate(5deg);
    animation-timing-function: ease-in-out;
  }
  70% {
    transform: translateX(-3px) rotate(360deg) translateY(0) rotate(-4deg);
    animation-timing-function: ease-in-out;
  }
  80%,
  100% {
    transform: translateX(0) rotate(360deg) translateY(0) rotate(0deg);
  }
}

// Turn over twice on the way down, then tip onto its side so it lies flat (in the pile the leaf is turned
// sideways by --rest, so tipping around its stem squashes it vertically) and flutter in the breeze until it fades
@keyframes tumble {
  0% {
    transform: rotateX(0deg) rotateY(0deg);
  }
  46% {
    transform: rotateX(720deg) rotateY(360deg);
    animation-timing-function: ease-out;
  }
  50% {
    transform: rotateX(720deg) rotateY(425deg);
    animation-timing-function: ease-in-out;
  }
  56%,
  66%,
  76% {
    transform: rotateX(726deg) rotateY(418deg);
    animation-timing-function: ease-in-out;
  }
  61%,
  71%,
  81% {
    transform: rotateX(714deg) rotateY(430deg);
    animation-timing-function: ease-in-out;
  }
  86%,
  100% {
    transform: rotateX(720deg) rotateY(425deg);
  }
}

// Wind blows every leaf to the right while it falls; it slows down to land, lies in the pile, then fades
@keyframes fall {
  0% {
    opacity: 0.9;
    transform: translate(var(--x0), -10vh) rotate(var(--tilt));
    animation-timing-function: linear;
  }
  40% {
    transform: translate(calc(var(--x0) + var(--drift) * 0.85), 75vh) rotate(var(--tilt));
    animation-timing-function: ease-out;
  }
  50%,
  85% {
    opacity: 0.9;
    transform: translate(calc(var(--x0) + var(--drift)), calc(100vh - var(--size) * 0.75 - var(--lift)))
      rotate(var(--rest));
  }
  100% {
    opacity: 0;
    transform: translate(calc(var(--x0) + var(--drift)), calc(100vh - var(--size) * 0.75 - var(--lift)))
      rotate(var(--rest));
  }
}

@for $i from 1 through 20 {
  .leaf:nth-child(#{$i}) {
    $duration: #{14 + math.random(8)}s;
    $delay: -#{math.random(22)}s;
    --size: #{2.8 + math.random(12) * 0.1}rem;
    --x0: #{math.random(90) - 25}vw;
    --drift: #{15 + math.random(20)}vw;
    --tilt: #{math.random(60) - 30}deg;
    --rest: #{if(math.random(2) == 1, 1, -1) * (74 + math.random(32))}deg;
    --lift: #{math.random(16) - 4}px;
    --amp: #{2 + math.random(4)}vw;
    --loop: #{if(math.random(3) == 1, 4vw, 0vw)};
    animation: fall $duration infinite $delay;

    .swirl {
      animation: swirl $duration infinite $delay;
    }

    .tumble {
      animation: tumble $duration infinite $delay;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .falling-leaves {
    display: none;
  }
}
</style>
